import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  alpha
} from '@mui/material';
import { Icon } from '@iconify/react';

interface NotificationPermissionDialogProps {
  open: boolean;
  onClose: () => void;
  onAllow: () => void;
}

const BENEFITS = [
  {
    icon: 'mdi:message-badge-outline',
    title: 'Мгновенные сообщения',
    text: 'Узнавайте о новых сообщениях от рекрутеров сразу, даже если вкладка свёрнута.'
  },
  {
    icon: 'mdi:calendar-clock-outline',
    title: 'Приглашения на встречи',
    text: 'Не пропускайте назначенные видеоинтервью.'
  },
  {
    icon: 'mdi:bell-off-outline',
    title: 'Без лишнего шума',
    text: 'Только самые важные уведомления'
  }
] as const;

export default function NotificationPermissionDialog({
  open,
  onClose,
  onAllow
}: NotificationPermissionDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack spacing={3} alignItems="center">
          <Box
            sx={(theme) => ({
              width: 64,
              height: 64,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main'
            })}
          >
            <Icon icon="mdi:bell-ring-outline" width={32} height={32} />
          </Box>

          <Stack spacing={1} alignItems="center" textAlign="center">
            <Typography variant="h6" fontWeight={700} sx={{ color: 'grey.900' }}>
              Включить уведомления?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Разрешите браузеру показывать уведомления, чтобы не пропустить важные сообщения от
              работодателей и приглашения на интервью.
            </Typography>
          </Stack>

          <Stack spacing={1.5} sx={{ width: '100%' }}>
            {BENEFITS.map((b) => (
              <Stack key={b.title} direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={(theme) => ({
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main'
                  })}
                >
                  <Icon icon={b.icon} width={18} height={18} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                    {b.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {b.text}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            size="large"
            fullWidth
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 700,
              borderColor: 'grey.300',
              color: 'text.primary',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
            }}
          >
            Не сейчас
          </Button>
          <Button
            onClick={onAllow}
            variant="contained"
            size="large"
            fullWidth
            sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700 }}
          >
            Разрешить
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
